import React, { useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

/**
 * Full-width advisor intro video block with custom play overlay.
 * File lives in /frontend/public/media/ so it is served statically.
 */
export default function AdvisorVideo() {
    const videoRef = useRef(null);
    const [playing, setPlaying] = useState(false);

    const toggle = () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) {
            v.play();
            setPlaying(true);
        } else {
            v.pause();
            setPlaying(false);
        }
    };

    return (
        <section
            data-testid="advisor-video-section"
            className="relative py-14 md:py-20 bg-brand-bg"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-brand-accent" />
                            <span className="text-xs uppercase tracking-[0.24em] text-brand-accent font-bold">
                                Meet your adviser
                            </span>
                        </div>
                        <h2 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-primary leading-[1.05]">
                            A quick word,
                            <br />
                            <em className="italic font-light text-brand-accent">
                                straight from us.
                            </em>
                        </h2>
                        <p className="mt-6 text-lg text-brand-muted leading-relaxed max-w-lg">
                            First-time buyer? Here's a short walk-through of how
                            we guide you from enquiry to the keys — at your pace,
                            on your terms.
                        </p>
                        <button
                            onClick={toggle}
                            data-testid="advisor-video-play-inline"
                            className="mt-8 inline-flex items-center gap-3 bg-brand-primary text-white px-6 py-3.5 rounded-full text-sm font-bold hover:bg-brand-primaryHover transition-colors"
                        >
                            {playing ? <Pause size={16} /> : <Play size={16} />}
                            {playing ? "Pause video" : "Play introduction"}
                        </button>
                    </div>

                    <div className="md:col-span-7">
                        <div className="relative overflow-hidden rounded-[20px] border border-brand-border shadow-[0_35px_70px_-25px_rgba(30,79,138,0.35)] bg-black">
                            <video
                                ref={videoRef}
                                data-testid="advisor-video-player"
                                className="w-full h-auto block"
                                playsInline
                                preload="metadata"
                                onPlay={() => setPlaying(true)}
                                onPause={() => setPlaying(false)}
                                onEnded={() => setPlaying(false)}
                                poster=""
                                onClick={toggle}
                            >
                                <source
                                    src="/media/advisor-first-home-buyer.mp4"
                                    type="video/mp4"
                                />
                            </video>
                            {!playing && (
                                <button
                                    onClick={toggle}
                                    data-testid="advisor-video-play-overlay"
                                    aria-label="Play advisor video"
                                    className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/50 via-black/15 to-black/5 group"
                                >
                                    <span className="h-20 w-20 md:h-24 md:w-24 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-105 transition-transform">
                                        <Play
                                            size={32}
                                            className="text-brand-primary ml-1"
                                            fill="currentColor"
                                        />
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
